import React from 'react';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined';
import { Box, Button, CircularProgress, Radio, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { getDrvPrimaryTypes, type DrvPrimaryType } from '@/lib/api/drv';
import {
  getMealPlanForDisplay,
  getMealPlanNutritionalAnalysis,
  getNutritionalAnalysisMealPlanNames,
  hideMealPlanNutrients,
  unhideMealPlanNutrients,
  updateFinalizedMealPlanItem,
} from '@/lib/api/mealPlans';
import type { FinalizedMeal, FinalizedMealPlanData, NutritionalAnalysisMealPlanNameItem } from '@/types/meal-plan';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { AnalysisHeaderControls } from './analysis/AnalysisHeaderControls';
import { AnalysisKpiGrid } from './analysis/AnalysisKpiGrid';
import { EnergyByMealCard } from './analysis/EnergyByMealCard';
import { MacroDistributionCard } from './analysis/MacroDistributionCard';
import { MacronutrientAnalysisSection, MacronutrientMacroTabSurfaceHeader } from './analysis/MacronutrientAnalysisSection';
import { MicronutrientsRdaSection } from './analysis/MicronutrientsRdaSection';
import {
  analysisHeaderDayCount,
  analysisTabs,
  buildAnalysisData,
  buildAnalysisHeaderSubtitleLine,
  buildNutrientReportTabHeaderSubtitle,
  buildMacroRingSegments,
  colors,
  macroAnalysisFigmaSegmentColors,
  buildSubtitleParts,
  safeNumber,
  type ClientDetailsV2AnalysisTabProps,
} from './analysis/analysis.shared';
import {
  deriveAnalysisDayCountFromNutritionalAnalysis,
  deriveAnalysisDayLabelsFromNutritionalAnalysis,
  deriveHiddenNutrientKeysFromNutritionalAnalysis,
  deriveMicrosViewFromNutritionalAnalysis,
  mergeMealPlanNutritionalAnalysisPayload,
} from './analysis/mergeMealPlanNutritionalAnalysis';
import { applyDrvFallbackTargetsToAnalysisData } from './analysis/analysisDrvKpiTargets';

const analysisGateAccent = '#0B89B7';
const backToMealPlanLabel = 'Back to Meal Plan';

type AnalysisGateVariant = 'no-meal-plans' | 'no-client';
type MealPlanReturnCategory = 'manual' | 'auto' | 'ai' | 'generic';
type GenericAnalysisMealChoice = {
  optionGroupId: string;
  label: string;
  itemId: string;
  itemNames: string[];
};
type GenericAnalysisMealSlot = {
  key: string;
  mealName: string;
  selectedOptionGroupId: string;
  choices: GenericAnalysisMealChoice[];
};

function resolveMealPlanReturnCategory(item?: NutritionalAnalysisMealPlanNameItem): MealPlanReturnCategory | null {
  if (!item) return null;

  const structured = [
    item.name,
    item.planType,
    item.generationType,
    item.generationSource,
    item.status,
  ]
    .map((value) => String(value ?? '').trim().toLowerCase())
    .filter(Boolean)
    .join(' ');
  const hasAiProvider = item.aiProvider != null && String(item.aiProvider).trim() !== '';

  if (structured.includes('generic') || structured.includes('template')) return 'generic';
  if (structured.includes('ai') || hasAiProvider) return 'ai';
  if (structured.includes('automated') || structured.includes('auto')) return 'auto';
  return 'manual';
}

function shouldLoadMealChoicePlan(item?: NutritionalAnalysisMealPlanNameItem): boolean {
  if (!item) return false;
  if (resolveMealPlanReturnCategory(item) === 'generic') return true;

  const name = String(item.name ?? '').trim().toLowerCase();
  if (name.includes('generic')) return true;

  const planType = String(item.planType ?? '').trim().toLowerCase();
  const generationType = String(item.generationType ?? '').trim().toLowerCase();
  const generationSource = String(item.generationSource ?? '').trim().toLowerCase();
  const status = String(item.status ?? '').trim().toLowerCase();

  return (
    (planType === 'finalized' || status === 'active' || status === 'finalized')
    && (generationType === 'manual' || generationSource === 'manual')
  );
}

function readDisplayString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toMealSlotLabel(value: string): string {
  const cleaned = value.trim().replace(/[_-]+/g, ' ');
  if (!cleaned) return 'Meal';
  return cleaned
    .split(/\s+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function buildGenericMealChoiceLabel(index: number): string {
  return `Meal choice ${index + 1}`;
}

function getMealChoiceItemId(item: FinalizedMeal): string {
  const record = item as FinalizedMeal & { id?: string; ingredientId?: string };
  return readDisplayString(item.itemId)
    || readDisplayString(record.id)
    || readDisplayString(record.ingredientId)
    || readDisplayString(item.recipeId);
}

function getMealChoiceItemName(item: FinalizedMeal): string {
  const record = item as FinalizedMeal & { name?: string; title?: string };
  return readDisplayString(item.recipeName)
    || readDisplayString(record.name)
    || readDisplayString(record.title)
    || 'Meal item';
}

function collectMealChoiceItems(meal: FinalizedMeal): FinalizedMeal[] {
  const mealRecord = meal as FinalizedMeal & { items?: FinalizedMeal[] };
  const rawItems = [
    ...(Array.isArray(mealRecord.items) ? mealRecord.items : []),
    ...(Array.isArray(meal.recipes) ? meal.recipes : []),
  ];
  const seen = new Set<string>();
  return rawItems.filter((item, index) => {
    const optionGroupId = readDisplayString(item.optionGroupId);
    if (!optionGroupId) return false;
    const key = `${optionGroupId}:${getMealChoiceItemId(item) || getMealChoiceItemName(item) || index}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function deriveGenericAnalysisMealSlots(
  mealPlan: FinalizedMealPlanData | null,
  selectedDayIndex: number,
): GenericAnalysisMealSlot[] {
  const day = mealPlan?.days?.[selectedDayIndex] ?? mealPlan?.days?.[0];
  if (!day?.meals) return [];

  return Object.entries(day.meals)
    .sort(([, a], [, b]) => {
      const aOrder = Number(a.mealOrder ?? (a as FinalizedMeal & { slotOrder?: number }).slotOrder ?? 0);
      const bOrder = Number(b.mealOrder ?? (b as FinalizedMeal & { slotOrder?: number }).slotOrder ?? 0);
      return (Number.isFinite(aOrder) && aOrder > 0 ? aOrder : 999)
        - (Number.isFinite(bOrder) && bOrder > 0 ? bOrder : 999);
    })
    .map(([mealKey, meal]) => {
      const items = collectMealChoiceItems(meal);
      if (items.length === 0) return null;

      const groups: GenericAnalysisMealChoice[] = [];
      const groupIndexById = new Map<string, number>();
      items.forEach((item) => {
        const optionGroupId = readDisplayString(item.optionGroupId);
        if (!optionGroupId) return;

        const existingIndex = groupIndexById.get(optionGroupId);
        const itemId = getMealChoiceItemId(item);
        const itemName = getMealChoiceItemName(item);
        if (existingIndex !== undefined) {
          const group = groups[existingIndex];
          if (!group.itemId && itemId) group.itemId = itemId;
          if (itemName && !group.itemNames.includes(itemName)) group.itemNames.push(itemName);
          return;
        }

        groupIndexById.set(optionGroupId, groups.length);
        groups.push({
          optionGroupId,
          label: buildGenericMealChoiceLabel(groups.length),
          itemId,
          itemNames: itemName ? [itemName] : [],
        });
      });

      if (groups.length === 0) return null;
      const selectedOptionGroupId =
        readDisplayString(meal.selectedOptionGroupId)
        || readDisplayString(items[0]?.selectedOptionGroupId)
        || groups[0].optionGroupId;
      return {
        key: `${day.day || selectedDayIndex + 1}:${mealKey}`,
        mealName: toMealSlotLabel(mealKey),
        selectedOptionGroupId,
        choices: groups,
      };
    })
    .filter((slot): slot is GenericAnalysisMealSlot => Boolean(slot));
}

function AnalysisGateCard({ variant }: { variant: AnalysisGateVariant }) {
  const isNoClient = variant === 'no-client';
  const icon = isNoClient ? (
    <PersonOffOutlinedIcon sx={{ fontSize: 28, color: analysisGateAccent }} aria-hidden />
  ) : (
    <RestaurantMenuOutlinedIcon sx={{ fontSize: 28, color: analysisGateAccent }} aria-hidden />
  );

  const title = isNoClient ? 'Unable to load client' : 'No meal plan yet';
  const message = isNoClient
    ? 'Refresh the page or open this client again from your list.'
    : 'Add a meal plan for this client, then return here to view nutrition analysis.';

  return (
    <Box
      sx={{
        mt: { xs: 2, sm: 2.25 },
        minWidth: 0,
        display: 'flex',
        justifyContent: 'center',
        px: { xs: 1.5, sm: 2 },
      }}
    >
      <Stack
        alignItems="center"
        spacing={2}
        sx={{
          width: '100%',
          maxWidth: 420,
          py: { xs: 3.5, sm: 4 },
          px: { xs: 2.5, sm: 3 },
          borderRadius: '14px',
          border: '1px solid #E4E7EC',
          bgcolor: '#fff',
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: '14px',
            bgcolor: '#EAF8FD',
          }}
        >
          {icon}
        </Box>
        <Typography
          component="h2"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '1.0625rem',
            lineHeight: 1.35,
            letterSpacing: '-0.02em',
            color: '#101828',
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '0.875rem',
            lineHeight: 1.5,
            color: '#667085',
            maxWidth: 360,
          }}
        >
          {message}
        </Typography>
      </Stack>
    </Box>
  );
}

export const ClientDetailsV2AnalysisTab: React.FC<ClientDetailsV2AnalysisTabProps> = ({ client, clientDisplayName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const requestedMealPlanId = React.useMemo(
    () => (searchParams.get('mealPlanId') || searchParams.get('planId') || '').trim(),
    [searchParams],
  );
  const requestedDayNumber = React.useMemo(() => {
    const dayParam = Number(searchParams.get('day') || searchParams.get('dayNumber') || '');
    return Number.isFinite(dayParam) && dayParam > 0 ? Math.floor(dayParam) : null;
  }, [searchParams]);
  const requestedDayIndex = requestedDayNumber ? requestedDayNumber - 1 : 0;
  const [activeAnalysisTab, setActiveAnalysisTab] = React.useState(0);
  /** 0 = By the Day, 1 = By the plan (analysis header). */
  const [viewMode, setViewMode] = React.useState(0);
  const [selectedDayIndex, setSelectedDayIndex] = React.useState(requestedDayIndex);
  const [mealPlans, setMealPlans] = React.useState<NutritionalAnalysisMealPlanNameItem[]>([]);
  const [selectedMealPlanId, setSelectedMealPlanId] = React.useState('');
  const [mealPlansLoading, setMealPlansLoading] = React.useState(true);
  const [nutritionalAnalysisResponse, setNutritionalAnalysisResponse] = React.useState<unknown>(null);
  const [nutritionalAnalysisLoading, setNutritionalAnalysisLoading] = React.useState(false);
  const [genericChoicePlan, setGenericChoicePlan] = React.useState<FinalizedMealPlanData | null>(null);
  const [genericChoiceLoading, setGenericChoiceLoading] = React.useState(false);
  const [updatingGenericChoiceKey, setUpdatingGenericChoiceKey] = React.useState('');
  const [drvPrimaryTypes, setDrvPrimaryTypes] = React.useState<DrvPrimaryType[]>([]);
  const [exportReportLoading, setExportReportLoading] = React.useState(false);
  const [prepareOffscreenReport, setPrepareOffscreenReport] = React.useState(false);
  const [pdfExportKind, setPdfExportKind] = React.useState<'macros' | 'nutrient-report' | null>(null);
  const offscreenReportRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!requestedDayNumber) return;
    setViewMode(0);
    setSelectedDayIndex(requestedDayIndex);
  }, [requestedDayIndex, requestedDayNumber]);

  React.useEffect(() => {
    let cancelled = false;
    const source = client?.drvRequirements?.source || client?.drvSource || undefined;
    (async () => {
      try {
        const res = await getDrvPrimaryTypes({ limit: 200, ...(source ? { source } : {}) });
        if (!cancelled) setDrvPrimaryTypes(res.items ?? []);
      } catch {
        if (!cancelled) setDrvPrimaryTypes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client.drvRequirements?.source, client.drvSource, client.id]);

  React.useEffect(() => {
    let cancelled = false;
    const clientId = client.id?.trim();
    if (!clientId) {
      setMealPlans([]);
      setSelectedMealPlanId('');
      setMealPlansLoading(false);
      return undefined;
    }

    setMealPlansLoading(true);
    (async () => {
      try {
        const rows = await getNutritionalAnalysisMealPlanNames({ clientId });
        if (cancelled) return;
        setMealPlans(rows);
        setSelectedMealPlanId((prev) => {
          if (requestedMealPlanId && rows.some((r) => r.id === requestedMealPlanId)) return requestedMealPlanId;
          if (prev && rows.some((r) => r.id === prev)) return prev;
          return rows[0]?.id ?? '';
        });
      } catch (err) {
        if (!cancelled) {
          setMealPlans([]);
          setSelectedMealPlanId('');
          showErrorToast(err instanceof Error ? err : new Error('Failed to load meal plans'), 'Meal plans');
        }
      } finally {
        if (!cancelled) setMealPlansLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client.id, requestedMealPlanId]);

  React.useEffect(() => {
    if (!selectedMealPlanId.trim()) {
      setNutritionalAnalysisResponse(null);
      setNutritionalAnalysisLoading(false);
      return undefined;
    }

    let cancelled = false;
    setNutritionalAnalysisLoading(true);
    setNutritionalAnalysisResponse(null);

    (async () => {
      try {
        const res = await getMealPlanNutritionalAnalysis(selectedMealPlanId);
        if (!cancelled) setNutritionalAnalysisResponse(res);
      } catch (err) {
        if (!cancelled) {
          setNutritionalAnalysisResponse(null);
          showErrorToast(
            err instanceof Error ? err : new Error('Failed to load nutritional analysis'),
            'Nutritional analysis',
          );
        }
      } finally {
        if (!cancelled) setNutritionalAnalysisLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedMealPlanId]);

  const selectedMealPlan = React.useMemo(
    () => mealPlans.find((item) => item.id === selectedMealPlanId),
    [mealPlans, selectedMealPlanId],
  );
  const selectedMealPlanCategory = React.useMemo(
    () => resolveMealPlanReturnCategory(selectedMealPlan),
    [selectedMealPlan],
  );
  const shouldLoadMealChoices = React.useMemo(
    () => shouldLoadMealChoicePlan(selectedMealPlan),
    [selectedMealPlan],
  );

  React.useEffect(() => {
    const id = selectedMealPlanId.trim();
    if (!id || !shouldLoadMealChoices) {
      setGenericChoicePlan(null);
      setGenericChoiceLoading(false);
      return undefined;
    }

    let cancelled = false;
    setGenericChoiceLoading(true);
    (async () => {
      try {
        const res = await getMealPlanForDisplay(id);
        if (cancelled) return;
        if (!res.success || !res.data) {
          throw new Error(res.message || 'Unable to load meal choices');
        }
        setGenericChoicePlan(res.data);
      } catch (err) {
        if (!cancelled) {
          setGenericChoicePlan(null);
          showErrorToast(err instanceof Error ? err : new Error('Unable to load meal choices'), 'Meal choices');
        }
      } finally {
        if (!cancelled) setGenericChoiceLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedMealPlanId, shouldLoadMealChoices]);

  const baseAnalysisData = React.useMemo(() => buildAnalysisData(client), [client]);

  const analysisDataWithDrvTargets = React.useMemo(
    () => applyDrvFallbackTargetsToAnalysisData(baseAnalysisData, client, drvPrimaryTypes),
    [baseAnalysisData, client, drvPrimaryTypes],
  );

  const analysisDayCount = React.useMemo(() => {
    const n = deriveAnalysisDayCountFromNutritionalAnalysis(nutritionalAnalysisResponse);
    return n ?? Math.max(analysisHeaderDayCount, requestedDayNumber ?? 1);
  }, [nutritionalAnalysisResponse, requestedDayNumber]);
  const analysisDayLabels = React.useMemo(
    () => deriveAnalysisDayLabelsFromNutritionalAnalysis(nutritionalAnalysisResponse) ?? [],
    [nutritionalAnalysisResponse],
  );

  React.useEffect(() => {
    setSelectedDayIndex((d) => {
      const next = requestedDayNumber ? requestedDayIndex : d;
      return Math.min(Math.max(0, next), Math.max(0, analysisDayCount - 1));
    });
  }, [analysisDayCount, requestedDayIndex, requestedDayNumber]);

  const analysisData = React.useMemo(() => {
    const merged = mergeMealPlanNutritionalAnalysisPayload(analysisDataWithDrvTargets, nutritionalAnalysisResponse, {
      viewMode,
      selectedDayIndex,
    });
    /** Merge adds KPI rows (e.g. MUFA/PUFA) with `target: 0`; DRV + primary-types fill those on a second pass. */
    return applyDrvFallbackTargetsToAnalysisData(merged, client, drvPrimaryTypes);
  }, [analysisDataWithDrvTargets, nutritionalAnalysisResponse, viewMode, selectedDayIndex, client, drvPrimaryTypes]);
  const microsView = React.useMemo(
    () => deriveMicrosViewFromNutritionalAnalysis(nutritionalAnalysisResponse, { viewMode, selectedDayIndex }),
    [nutritionalAnalysisResponse, viewMode, selectedDayIndex],
  );
  const hiddenNutrientKeys = React.useMemo(
    () => deriveHiddenNutrientKeysFromNutritionalAnalysis(nutritionalAnalysisResponse),
    [nutritionalAnalysisResponse],
  );
  const routeAnalysisBackPath = React.useMemo(() => {
    const state = location.state as { analysisBackPath?: unknown } | null;
    const rawPath = typeof state?.analysisBackPath === 'string' ? state.analysisBackPath.trim() : '';
    return rawPath.startsWith('/') ? rawPath : '';
  }, [location.state]);
  const fallbackAnalysisBackPath = React.useMemo(() => {
    const clientId = client.id?.trim();
    if (!clientId) return '';

    const mealPlanId = selectedMealPlanId.trim() || requestedMealPlanId;
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.delete('planId');
    nextSearchParams.delete('dayNumber');
    if (selectedMealPlanCategory) nextSearchParams.set('category', selectedMealPlanCategory);
    if (mealPlanId) nextSearchParams.set('mealPlanId', mealPlanId);
    if (selectedDayIndex >= 0) nextSearchParams.set('day', String(selectedDayIndex + 1));

    const query = nextSearchParams.toString();
    return `/clients/${encodeURIComponent(clientId)}/details-v2/meal-plan${query ? `?${query}` : ''}`;
  }, [client.id, requestedMealPlanId, searchParams, selectedDayIndex, selectedMealPlanCategory, selectedMealPlanId]);
  const shouldShowBackToMealPlan = Boolean(routeAnalysisBackPath || requestedMealPlanId);
  const handleBackToMealPlan = React.useCallback(() => {
    const target = routeAnalysisBackPath || fallbackAnalysisBackPath;
    if (!target) return;
    navigate(target);
  }, [fallbackAnalysisBackPath, navigate, routeAnalysisBackPath]);

  const genericChoiceSlots = React.useMemo(
    () => deriveGenericAnalysisMealSlots(genericChoicePlan, viewMode === 0 ? selectedDayIndex : 0),
    [genericChoicePlan, selectedDayIndex, viewMode],
  );

  const handleGenericChoiceChange = React.useCallback(
    async (slot: GenericAnalysisMealSlot, choice: GenericAnalysisMealChoice) => {
      const id = selectedMealPlanId.trim();
      if (!id || !choice.itemId || choice.optionGroupId === slot.selectedOptionGroupId) return;

      const updateKey = `${slot.key}:${choice.optionGroupId}`;
      setUpdatingGenericChoiceKey(updateKey);
      setNutritionalAnalysisLoading(true);
      try {
        const response = await updateFinalizedMealPlanItem(id, {
          itemId: choice.itemId,
          selectedOptionGroupId: choice.optionGroupId,
        });
        if (!response.success) {
          throw new Error(response.message || 'Unable to update meal choice');
        }

        const [detailRes, analysisRes] = await Promise.all([
          getMealPlanForDisplay(id),
          getMealPlanNutritionalAnalysis(id),
        ]);
        if (detailRes.success && detailRes.data) {
          setGenericChoicePlan(detailRes.data);
        }
        setNutritionalAnalysisResponse(analysisRes);
      } catch (err) {
        showErrorToast(err instanceof Error ? err : new Error('Unable to update meal choice'), 'Meal choices');
      } finally {
        setUpdatingGenericChoiceKey('');
        setNutritionalAnalysisLoading(false);
      }
    },
    [selectedMealPlanId],
  );

  const handleNutrientVisibilityChange = React.useCallback(
    async ({ nutrientKey, hide }: { nutrientKey: string; hide: boolean }) => {
      const id = selectedMealPlanId.trim();
      const key = nutrientKey.trim().toLowerCase();
      if (!id || !key) return;
      try {
        if (hide) {
          await hideMealPlanNutrients(id, { nutrients: [key] });
        } else {
          await unhideMealPlanNutrients(id, { nutrients: [key] });
        }
        const res = await getMealPlanNutritionalAnalysis(id);
        setNutritionalAnalysisResponse(res);
        if (hide) {
          showSuccessToast(
            'Nutrient hidden successfully',
            'This nutrient is now hidden from plan nutrition views. You can tap Unhide to show it again.',
          );
        } else {
          showSuccessToast(
            'Nutrient visible again',
            'This nutrient is shown in plan nutrition views.',
          );
        }
      } catch (err) {
        console.error('Nutrient visibility error:', err);
        showErrorToast(
          err instanceof Error ? err : new Error('Could not update nutrient visibility'),
          'Nutrient visibility',
        );
        throw err;
      }
    },
    [selectedMealPlanId],
  );

  const activeTabLabel = analysisTabs[activeAnalysisTab] ?? analysisTabs[0];
  const subtitleLine = React.useMemo(() => {
    if (activeTabLabel === 'Nutrient Report') {
      return buildNutrientReportTabHeaderSubtitle(client);
    }
    return buildAnalysisHeaderSubtitleLine(client, clientDisplayName);
  }, [activeTabLabel, client, clientDisplayName]);
  const subtitleParts = React.useMemo(() => buildSubtitleParts(client, clientDisplayName), [client, clientDisplayName]);
  const macroRingSegments = React.useMemo(
    () => buildMacroRingSegments(analysisData.macroShare, macroAnalysisFigmaSegmentColors),
    [analysisData.macroShare],
  );
  const freeSugarTarget = React.useMemo(() => {
    const fromKpi = analysisData.kpis.find((k) => k.label.trim().toLowerCase() === 'free sugars')?.target;
    if (Number.isFinite(fromKpi) && (fromKpi as number) > 0) return fromKpi as number;
    return safeNumber(client.clientGoals?.freeSugars?.value, 30);
  }, [analysisData.kpis, client.clientGoals?.freeSugars?.value]);
  const isAllCharts = activeTabLabel === 'All Charts';
  const isMacros = activeTabLabel === 'Macros';
  const isDietLog = activeTabLabel === 'Diet Log';
  const showKpiCards =
    activeTabLabel !== 'Nutrient Report' &&
    activeTabLabel !== 'Diet Log' &&
    activeTabLabel !== 'Micronutrients';
  const showMacroCards = isMacros;
  const showMacronutrientTable = isMacros || isDietLog || activeTabLabel === 'Nutrient Report';
  const showMicronutrients = activeTabLabel === 'Micronutrients';

  React.useEffect(() => {
    if (!prepareOffscreenReport) return;
    let cancelled = false;
    const run = async () => {
      try {
        // Let fonts/layout settle before html2canvas to avoid PDF text metric drift.
        if (document.fonts?.ready) {
          try {
            await document.fonts.ready;
          } catch {
            /* ignore font readiness failures */
          }
        }
        await new Promise((resolve) => window.requestAnimationFrame(() => resolve(null)));
        await new Promise((resolve) => window.requestAnimationFrame(() => resolve(null)));
        const target = offscreenReportRef.current;
        if (!target) throw new Error('Nutrient report is not ready for export');

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 8;
        const contentWidth = pageWidth - margin * 2;
        const contentHeight = pageHeight - margin * 2;
        const renderScale = 2;
        const fullCanvas = await html2canvas(target, {
          scale: renderScale,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          scrollX: 0,
          scrollY: 0,
          windowWidth: target.scrollWidth,
          windowHeight: target.scrollHeight,
        });
        if (cancelled) return;

        const pxPerCss = fullCanvas.height / Math.max(1, target.scrollHeight);
        /** Slightly under-fill each PDF page so html2canvas slices do not clip table edges. */
        const pageCapacitySafetyPx = Math.max(8, Math.round(pxPerCss * 18));
        const pageCapacityPx = Math.max(
          1,
          Math.floor((contentHeight * fullCanvas.width) / contentWidth) - pageCapacitySafetyPx,
        );
        const blocks = Array.from(target.querySelectorAll<HTMLElement>('[data-pdf-block="true"]')).sort((a, b) => {
          const ta = a.getBoundingClientRect().top;
          const tb = b.getBoundingClientRect().top;
          return ta - tb;
        });

        const toCanvasPx = (cssPx: number) => (cssPx / Math.max(1, target.scrollHeight)) * fullCanvas.height;
        const targetRect = target.getBoundingClientRect();

        const blockEdgeBufferPx = Math.max(10, Math.round(pxPerCss * 8));
        type PdfBlockRange = { start: number; end: number; forcePageBreakBefore: boolean; avoidSplit: boolean };
        const rawBlockRanges: PdfBlockRange[] = blocks
          .map((block) => {
            const rect = block.getBoundingClientRect();
            const startCss = rect.top - targetRect.top;
            const endCss = rect.bottom - targetRect.top;
            const forcePageBreakBefore = block.getAttribute('data-pdf-page-break-before') === 'true';
            /** Boolean attribute / empty string must count as true so tall blocks are not vertically chunked (e.g. pie vs legend). */
            const pdfAvoid = block.getAttribute('data-pdf-avoid-split');
            const avoidSplit =
              block.hasAttribute('data-pdf-avoid-split') && pdfAvoid !== 'false' && pdfAvoid !== '0';
            return {
              start: Math.max(0, Math.floor(toCanvasPx(startCss)) - blockEdgeBufferPx),
              end: Math.min(fullCanvas.height, Math.ceil(toCanvasPx(endCss)) + blockEdgeBufferPx),
              forcePageBreakBefore,
              avoidSplit,
            };
          })
          .filter((range) => range.end > range.start)
          .sort((a, b) => a.start - b.start);

        // Do not merge adjacent blocks: merging made one "super-range" taller than a page and
        // triggered vertical splits through the middle of tables. Each data-pdf-block stays its own range.
        const blockRanges = rawBlockRanges;

        const slices: Array<{ startPx: number; heightPx: number }> = [];
        let pageStart = 0;
        let pageEnd = pageStart;

        const pushSlice = (start: number, end: number) => {
          const safeStart = Math.max(0, Math.min(fullCanvas.height, Math.floor(start)));
          const safeEnd = Math.max(safeStart + 1, Math.min(fullCanvas.height, Math.ceil(end)));
          slices.push({ startPx: safeStart, heightPx: safeEnd - safeStart });
        };

        const startNewPageAt = (nextStart: number) => {
          if (pageEnd > pageStart) pushSlice(pageStart, pageEnd);
          pageStart = nextStart;
          pageEnd = nextStart;
        };

        for (const range of blockRanges) {
          const blockStart = range.start;
          const blockEnd = range.end;
          const blockHeight = Math.max(1, blockEnd - blockStart);

          // Explicit break when a block sets data-pdf-page-break-before (e.g. future section breaks).
          if (range.forcePageBreakBefore && pageEnd > pageStart) {
            startNewPageAt(blockStart);
          }

          // If current page already has content and this block won't fit, start a new page at block start.
          if (pageEnd > pageStart && blockEnd - pageStart > pageCapacityPx) {
            startNewPageAt(blockStart);
          }

          // If a single block is taller than one page: never slice through tables/sections marked avoid-split.
          if (blockHeight > pageCapacityPx) {
            if (pageEnd > pageStart) startNewPageAt(blockStart);
            if (range.avoidSplit) {
              pushSlice(blockStart, blockEnd);
              pageStart = blockEnd;
              pageEnd = blockEnd;
              continue;
            }
            let blockCursor = blockStart;
            while (blockCursor < blockEnd) {
              const chunkEnd = Math.min(blockEnd, blockCursor + pageCapacityPx);
              pushSlice(blockCursor, chunkEnd);
              blockCursor = chunkEnd;
            }
            pageStart = blockEnd;
            pageEnd = blockEnd;
            continue;
          }

          pageEnd = Math.max(pageEnd, blockEnd);
        }

        // Include trailing content after the final block, if any.
        if (pageEnd < fullCanvas.height) {
          if (fullCanvas.height - pageStart > pageCapacityPx && pageEnd > pageStart) {
            pushSlice(pageStart, pageEnd);
            pageStart = pageEnd;
          }
          pageEnd = fullCanvas.height;
        }
        if (pageEnd > pageStart) pushSlice(pageStart, pageEnd);

        slices.forEach((slice, index) => {
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = fullCanvas.width;
          sliceCanvas.height = Math.min(slice.heightPx, fullCanvas.height - slice.startPx);
          const sliceCtx = sliceCanvas.getContext('2d');
          if (!sliceCtx) return;
          sliceCtx.drawImage(
            fullCanvas,
            0,
            slice.startPx,
            fullCanvas.width,
            sliceCanvas.height,
            0,
            0,
            fullCanvas.width,
            sliceCanvas.height,
          );
          const aspect = sliceCanvas.width > 0 ? sliceCanvas.height / sliceCanvas.width : 1;
          let imgWmm = contentWidth;
          let imgHmm = (sliceCanvas.height * contentWidth) / fullCanvas.width;
          if (imgHmm > contentHeight) {
            imgHmm = contentHeight;
            imgWmm = contentHeight / Math.max(aspect, 1e-6);
          }
          if (imgWmm > contentWidth) {
            imgWmm = contentWidth;
            imgHmm = contentWidth * aspect;
          }
          const xMm = margin + (contentWidth - imgWmm) / 2;
          if (index > 0) pdf.addPage();
          pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', xMm, margin, imgWmm, imgHmm);
        });

        const safeName = (clientDisplayName || 'client').trim().replace(/[^a-z0-9-_]+/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
        const slug = pdfExportKind === 'macros' ? 'macros' : 'nutrient-report';
        pdf.save(`${safeName || 'client'}-${slug}.pdf`);
      } catch (err) {
        showErrorToast(err instanceof Error ? err : new Error('Failed to export nutrient report'), 'Export report');
      } finally {
        if (!cancelled) {
          setPrepareOffscreenReport(false);
          setExportReportLoading(false);
          setPdfExportKind(null);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    prepareOffscreenReport,
    pdfExportKind,
    client,
    clientDisplayName,
    analysisData,
    macroRingSegments,
    subtitleParts,
    microsView,
    nutritionalAnalysisResponse,
    viewMode,
    selectedDayIndex,
    selectedMealPlanId,
  ]);

  const handleExportReport = React.useCallback(() => {
    if (exportReportLoading) return;
    const tab = analysisTabs[activeAnalysisTab] ?? analysisTabs[0];
    setPdfExportKind(tab === 'Macros' ? 'macros' : 'nutrient-report');
    setExportReportLoading(true);
    setPrepareOffscreenReport(true);
  }, [exportReportLoading, activeAnalysisTab]);

  const clientIdPresent = Boolean(client.id?.trim());

  if (!clientIdPresent) {
    return <AnalysisGateCard variant="no-client" />;
  }

  if (mealPlansLoading) {
    return (
      <Box
        role="status"
        aria-label="Loading meal plans"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 220,
          mt: 3,
        }}
      >
        <CircularProgress size={40} sx={{ color: colors.cyan }} />
      </Box>
    );
  }

  if (mealPlans.length === 0) {
    return <AnalysisGateCard variant="no-meal-plans" />;
  }

  return (
    <Box sx={{ mt: 2.25, display: 'grid', gap: 1.5, minWidth: 0 }}>
      <Box
        sx={{
          p: 0,
          minWidth: 0,
          bgcolor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          gap: isMacros ? '16px' : isAllCharts ? '12px' : 0,
        }}
      >
        <AnalysisHeaderControls
          mealPlans={mealPlans}
          selectedMealPlanId={selectedMealPlanId}
          onMealPlanChange={setSelectedMealPlanId}
          mealPlansLoading={mealPlansLoading}
          subtitleLine={subtitleLine}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          selectedDayIndex={selectedDayIndex}
          analysisDayCount={analysisDayCount}
          dayLabels={analysisDayLabels}
          onSelectedDayIndexChange={setSelectedDayIndex}
          nutritionalAnalysisLoading={nutritionalAnalysisLoading}
          activeAnalysisTab={activeAnalysisTab}
          onAnalysisTabChange={setActiveAnalysisTab}
          backButtonLabel={backToMealPlanLabel}
          onBackClick={shouldShowBackToMealPlan ? handleBackToMealPlan : undefined}
        />
        {genericChoiceLoading ? (
          <Box
            aria-label="Meal choice selector loading"
            sx={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              bgcolor: '#ffffff',
              px: 1.5,
              py: 1.25,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={16} sx={{ color: colors.cyan }} />
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: '#475467',
                }}
              >
                Loading meal choices
              </Typography>
            </Stack>
          </Box>
        ) : genericChoiceSlots.length > 0 ? (
          <Box
            aria-label="Meal choice selector"
            sx={{
              display: 'grid',
              gap: 1,
            }}
          >
            <Typography
              component="h3"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.84rem',
                fontWeight: 800,
                color: '#1f2937',
                lineHeight: 1.35,
              }}
            >
              Meal choices
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                gap: 1,
              }}
            >
              {genericChoiceSlots.map((slot) => (
                <Box
                  key={slot.key}
                  sx={{
                    minWidth: 0,
                    border: '1px solid #edf2f7',
                    borderRadius: '8px',
                    bgcolor: '#fbfdff',
                    px: 1,
                    py: 0.9,
                  }}
                >
                  <Typography
                    sx={{
                      mb: 0.75,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.76rem',
                      fontWeight: 800,
                      color: '#667085',
                      lineHeight: 1.25,
                    }}
                  >
                    {slot.mealName}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.8}>
                    {slot.choices.map((choice) => {
                      const selected = choice.optionGroupId === slot.selectedOptionGroupId;
                      const disabled = Boolean(updatingGenericChoiceKey) || !choice.itemId;
                      return (
                        <Box
                          component="label"
                          key={choice.optionGroupId}
                          sx={{
                            minWidth: 0,
                            flex: '1 1 180px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.6,
                            px: 0.1,
                            py: 0.2,
                            cursor: disabled ? 'default' : 'pointer',
                            opacity: disabled && !selected ? 0.58 : 1,
                          }}
                        >
                          <Radio
                            size="small"
                            name={`analysis-meal-choice-${slot.key}`}
                            value={choice.optionGroupId}
                            checked={selected}
                            disabled={disabled}
                            onChange={() => {
                              void handleGenericChoiceChange(slot, choice);
                            }}
                            inputProps={{ 'aria-label': `Use ${choice.label} for ${slot.mealName}` }}
                            sx={{
                              mt: -0.35,
                              p: 0.2,
                              color: '#98a2b3',
                              '&.Mui-checked': { color: '#2563eb' },
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.78rem',
                                fontWeight: 800,
                                color: '#1f2937',
                                lineHeight: 1.25,
                              }}
                            >
                              {choice.label}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.15,
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.72rem',
                                fontWeight: 500,
                                color: '#667085',
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {choice.itemNames.join(' + ') || 'Meal item'}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
        {isMacros ? (
          <MacronutrientMacroTabSurfaceHeader
            onExportReport={handleExportReport}
            exportReportLoading={exportReportLoading}
          />
        ) : null}
        {showKpiCards ? (
          <AnalysisKpiGrid analysisData={analysisData} appearance={isMacros ? 'macroFigma' : 'default'} />
        ) : null}
      </Box>

      {isAllCharts ? (
        <>
          <MacronutrientAnalysisSection
            analysisData={analysisData}
            macroRingSegments={macroRingSegments}
            tableVariant="default"
            client={client}
            clientDisplayName={clientDisplayName}
            subtitleParts={subtitleParts}
            appearance="default"
            microsView={microsView}
            nutritionalApiResponse={nutritionalAnalysisResponse}
            viewMode={viewMode}
            selectedDayIndex={selectedDayIndex}
            mealPlanId={selectedMealPlanId}
            onExportReport={handleExportReport}
            exportReportLoading={exportReportLoading}
            hiddenNutrientKeys={hiddenNutrientKeys}
            onNutrientVisibilityChange={handleNutrientVisibilityChange}
          />

          <Box
            sx={{
              display: 'grid',
              gap: '16px',
              alignItems: 'stretch',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'repeat(2, minmax(0, 1fr))',
              },
              minWidth: 0,
            }}
          >
            <MacroDistributionCard
              analysisData={analysisData}
              freeSugarTarget={freeSugarTarget}
              appearance="default"
            />
            <EnergyByMealCard analysisData={analysisData} viewMode={viewMode} />
          </Box>

          <MicronutrientsRdaSection microsView={microsView} />
        </>
      ) : (
        <>
          {showMacroCards ? (
            <Box
              sx={{
                display: 'grid',
                gap: '16px',
                alignItems: 'stretch',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: 'repeat(2, minmax(0, 1fr))',
                },
                minWidth: 0,
              }}
            >
              <MacroDistributionCard
                analysisData={analysisData}
                freeSugarTarget={freeSugarTarget}
                appearance="macroFigma"
              />
              <EnergyByMealCard analysisData={analysisData} appearance="macroFigma" viewMode={viewMode} />
            </Box>
          ) : null}

          {showMacronutrientTable ? (
            <MacronutrientAnalysisSection
              analysisData={analysisData}
              macroRingSegments={macroRingSegments}
              tableVariant={activeTabLabel === 'Nutrient Report' ? 'nutrition-report' : 'default'}
              client={client}
              clientDisplayName={clientDisplayName}
              subtitleParts={subtitleParts}
              appearance={isMacros ? 'macroFigma' : 'default'}
              microsView={microsView}
              nutritionalApiResponse={nutritionalAnalysisResponse}
              viewMode={viewMode}
              selectedDayIndex={selectedDayIndex}
              mealPlanId={selectedMealPlanId}
              onExportReport={handleExportReport}
              exportReportLoading={exportReportLoading}
              hideSurfaceHeader={isMacros}
              hiddenNutrientKeys={hiddenNutrientKeys}
              onNutrientVisibilityChange={handleNutrientVisibilityChange}
            />
          ) : null}
          {showMicronutrients ? <MicronutrientsRdaSection microsView={microsView} /> : null}
        </>
      )}

      {shouldShowBackToMealPlan ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'stretch', sm: 'flex-start' },
            pt: 1,
            pb: 0.5,
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={handleBackToMealPlan}
            startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: 15 }} />}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minHeight: 40,
              px: 2,
              py: 1,
              borderRadius: '8px',
              borderColor: '#d0d5dd',
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '20px',
              color: '#344054',
              bgcolor: '#ffffff',
              boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
              '& .MuiButton-startIcon': {
                mr: '6px',
              },
              '&:hover': {
                borderColor: '#98a2b3',
                bgcolor: '#f9fafb',
                boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
              },
            }}
          >
            {backToMealPlanLabel}
          </Button>
        </Box>
      ) : null}

      {prepareOffscreenReport ? (
        <Box
          sx={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: 1160,
            bgcolor: '#fff',
            p: 2,
            zIndex: -1,
            pointerEvents: 'none',
            display: 'flex',
            justifyContent: 'center',
          }}
          aria-hidden
        >
          <Box ref={offscreenReportRef} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <MacronutrientAnalysisSection
              analysisData={analysisData}
              macroRingSegments={macroRingSegments}
              tableVariant="nutrition-report"
              client={client}
              clientDisplayName={clientDisplayName}
              subtitleParts={subtitleParts}
              appearance={isMacros ? 'macroFigma' : 'default'}
              microsView={microsView}
              nutritionalApiResponse={nutritionalAnalysisResponse}
              viewMode={viewMode}
              selectedDayIndex={selectedDayIndex}
              mealPlanId={selectedMealPlanId}
              isPdfExport
              pdfExportVariant={pdfExportKind === 'macros' ? 'macros-only' : 'full'}
              hiddenNutrientKeys={hiddenNutrientKeys}
              onNutrientVisibilityChange={handleNutrientVisibilityChange}
              pdfMacroDistributionBlock={(
                <MacronutrientAnalysisSection
                  analysisData={analysisData}
                  macroRingSegments={macroRingSegments}
                  tableVariant="default"
                  client={client}
                  clientDisplayName={clientDisplayName}
                  subtitleParts={subtitleParts}
                  appearance={isMacros ? 'macroFigma' : 'default'}
                  microsView={microsView}
                  nutritionalApiResponse={nutritionalAnalysisResponse}
                  viewMode={1}
                  selectedDayIndex={selectedDayIndex}
                  mealPlanId={selectedMealPlanId}
                  isPdfExport
                  hiddenNutrientKeys={hiddenNutrientKeys}
                  onNutrientVisibilityChange={handleNutrientVisibilityChange}
                />
              )}
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
